import { get, ref, runTransaction } from 'firebase/database';
import { useEffect, useState } from 'react';
import { db } from './firebase';
import './styles.css';

// 숫자 천단위 콤마
const formatNumber = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// 이름 마스킹
const maskName = (name) => {
  if (name.length <= 2) return name[0] + '*';
  return name[0] + '*'.repeat(name.length - 2) + name[name.length - 1];
};

// 전화번호 마스킹
const maskPhone = (phone) => {
  if (phone.length < 7) return phone;
  return phone.substring(0, 3) + '****' + phone.substring(phone.length - 4);
};

// 날짜·시간 표시 (YY-MM-DD HH:mm)
const formatDateTime = () => {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  return `${yy}-${mm}-${dd} ${hh}:${min}`;
};

function App() {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [user, setUser] = useState(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  // 최초 성공 회원(해당 회원만 재조회 가능)
  const [allowedUser, setAllowedUser] = useState(null);

  // 실패 카운트 & 잠금 여부
  const [failCount, setFailCount] = useState(0);
  const [failLocked, setFailLocked] = useState(false);

  // 조회 시간 저장
  const [timestamp, setTimestamp] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('allowedUser');
    if (saved) {
      setAllowedUser(JSON.parse(saved));
    }

    const fc = localStorage.getItem('failCount');
    const lockedUntil = localStorage.getItem('failLockedUntil');

    if (fc) setFailCount(Number(fc));
    if (lockedUntil && Number(lockedUntil) > Date.now()) {
      setFailLocked(true);
    }
  }, []);

  // 조회 로그 저장
  const addStats = async () => {
    const today = new Date().toISOString().slice(0, 10);

    // totalSearch 증가
    runTransaction(ref(db, 'stats/totalSearch'), (current) => {
      return (current || 0) + 1;
    });

    // 일자별 증가
    runTransaction(ref(db, `stats/daily/${today}`), (current) => {
      return (current || 0) + 1;
    });
  };

  const searchPoint = async () => {
    if (failLocked) {
      alert('조회 실패 횟수 초과로 24시간 동안 조회가 제한됩니다.');
      return;
    }

    // 허용된 회원인지 확인
    if (allowedUser) {
      if (name !== allowedUser.name || phone !== allowedUser.phone) {
        alert('보안 정책으로 인해 최초 조회한 회원만 조회할 수 있습니다.');
        return;
      }
    }

    if (!name || !phone) {
      alert('이름과 전화번호를 모두 입력해주세요.');
      return;
    }

    setLoading(true);
    setSearched(false);

    const snapshot = await get(ref(db, 'users'));

    setTimeout(async () => {
      let found = null;

      if (snapshot.exists()) {
        const users = snapshot.val();
        Object.keys(users).forEach((key) => {
          const u = users[key];
          if (u.name === name.trim() && u.phoneNum === phone.trim()) {
            const total = u?.points?.reduce((acc, p) => acc + p.point, 0) || 0;
            found = { ...u, total };
          }
        });
      }

      setUser(found);
      setSearched(true);
      setLoading(false);

      if (found) {
        // 조회 시각 저장
        setTimestamp(formatDateTime());

        // 실패 횟수 초기화
        localStorage.setItem('failCount', '0');

        // 최초 성공 회원 저장
        if (!allowedUser) {
          const store = { name: found.name, phone: found.phoneNum };
          localStorage.setItem('allowedUser', JSON.stringify(store));
          setAllowedUser(store);
        }

        // 통계 저장
        addStats();
      } else {
        // 실패 +1
        const newFailCount = failCount + 1;
        setFailCount(newFailCount);
        localStorage.setItem('failCount', newFailCount.toString());

        if (newFailCount >= 5) {
          const lockTime = Date.now() + 24 * 60 * 60 * 1000;
          localStorage.setItem('failLockedUntil', lockTime.toString());
          setFailLocked(true);
          alert('조회 실패가 5회 초과하여 24시간 잠금됩니다.');
        }
      }
    }, 400);
  };

  return (
    <div className="container">
      {/* 상단 로고 */}
      <div className="brandBox">
        <img src="/logo192.png" className="brandLogo" alt="쌍교 로고" />
        <span className="brandName">쌍교숯불갈비 매장 멤버십</span>
      </div>

      <h1 className="title">멤버십 포인트 조회</h1>

      {/* 입력 영역 */}
      <div className="formBox">
        <label className="inputTitle">이름</label>
        <input
          className="inputField"
          type="text"
          placeholder="홍길동"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={failLocked}
        />

        <label className="inputTitle">전화번호</label>
        <input
          className="inputField"
          type="text"
          placeholder="01012345678"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={failLocked}
        />

        <button className="searchBtn" onClick={searchPoint} disabled={failLocked}>
          조회하기
        </button>

        {loading && <p className="loadingText">⏳ 조회 중입니다...</p>}

        {failLocked && (
          <p className="lockedText">조회 실패가 5회 초과하여 24시간 잠금되었습니다.</p>
        )}
      </div>

      {/* 조회 성공 */}
      {searched && user && (
        <div className="resultBox">
          <h3 className="resultName">{maskName(user.name)} 님 포인트 정보</h3>

          <p>전화번호: {maskPhone(user.phoneNum)}</p>

          <p>
            현재({timestamp}) 잔여 포인트: <b>{formatNumber(user.total)}</b>
          </p>

          <p className="warnText">
            * 해당 포인트는 실제 매장 조회 시 상이할 수 있습니다.(당일 포인트 적립/사용 내역은
            반영되지 않습니다.)
          </p>

          <div className="historyBox">
            <h4>포인트 내역</h4>
            <p className="prepareText">포인트 내역은 준비중입니다.</p>
          </div>
        </div>
      )}

      {/* 조회 실패 */}
      {searched && !user && (
        <div className="resultBox">
          <p className="noResult">일치하는 회원 정보가 없습니다.</p>
        </div>
      )}

      {/* 공지 */}
      <div className="noticeBox">
        <h4 className="noticeTitle">이용안내</h4>
        <p className="noticeText">
          매장에서 가입한 고객정보로 포인트 조회가 가능합니다. 조회가 되지 않을 경우 매장 또는
          카카오톡 채널로 문의해주세요.
        </p>
      </div>

      {/* 하단 버튼 2개 */}
      <div className="bottomButtons">
        <a href="https://www.ssangkyomall.com" target="_blank" rel="noreferrer" className="homeBtn">
          쌍교 홈페이지
        </a>
        <a
          className="kakaoBtn"
          href="http://pf.kakao.com/_lShlu/chat"
          target="_blank"
          rel="noopener noreferrer"
        >
          💬 카카오톡 문의
        </a>
      </div>
    </div>
  );
}

export default App;
